import * as React from "react";
import { Square } from "lucide-react";
import { Button } from "../../ui/button.js";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../ui/sheet.js";
import { formatMs } from "./TimerPill.js";
import type { ActiveTimer } from "./useTimers.js";

// ---------------------------------------------------------------------------
// TimerTray
// ---------------------------------------------------------------------------

export interface TimerTrayProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** All currently running (not-yet-fired) timers. */
  running: ActiveTimer[];
  /**
   * Map a stepId to a 1-based step number for display ("Step N").
   * Optional — omit or return null to hide step attribution.
   */
  stepNumberFor?: (stepId: string) => number | null;
  /** Extend a timer by addMs. Canonical call: onExtend(id, 60_000). */
  onExtend: (id: string, addMs: number) => void;
  /** Remove/stop a timer. */
  onStop: (id: string) => void;
}

/**
 * Bottom sheet listing every active timer.
 * Per-timer controls: +1m (onExtend) and stop ■ (onStop).
 * All interactive targets are ≥ 48 px (Button size="icon-xl" or size="cook").
 */
export function TimerTray({
  open,
  onOpenChange,
  running,
  stepNumberFor,
  onExtend,
  onStop,
}: TimerTrayProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="pb-safe">
        <SheetHeader>
          <SheetTitle>Running timers</SheetTitle>
        </SheetHeader>

        {running.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">
            No timers running.
          </p>
        ) : (
          <ul className="flex flex-col gap-3 px-4 pb-4">
            {running.map((timer) => {
              const remainingMs = Math.max(
                0,
                new Date(timer.endsAt).getTime() - Date.now(),
              );
              const stepNum = stepNumberFor ? stepNumberFor(timer.stepId) : null;

              return (
                <li
                  key={timer.id}
                  className="flex items-center justify-between gap-4 rounded-lg border border-border bg-card px-4 py-3"
                >
                  {/* Left: label + step attribution + countdown */}
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-semibold text-foreground truncate">
                      {timer.label}
                    </p>
                    {stepNum !== null && (
                      <p className="text-sm text-muted-foreground">
                        Step {stepNum}
                      </p>
                    )}
                    <p className="mt-0.5 text-2xl font-mono tabular-nums text-foreground">
                      {formatMs(remainingMs)}
                    </p>
                  </div>

                  {/* Right: +1m and stop controls */}
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="cook"
                      variant="outline"
                      aria-label={`Add 1 minute to ${timer.label}`}
                      onClick={() => onExtend(timer.id, 60_000)}
                    >
                      +1m
                    </Button>

                    <Button
                      size="icon-xl"
                      variant="destructive"
                      aria-label={`Stop ${timer.label}`}
                      onClick={() => onStop(timer.id)}
                    >
                      <Square className="size-5" aria-hidden />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </SheetContent>
    </Sheet>
  );
}
